import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { 
  Calendar, Clock, Users, Tractor, Plus, Bell, Settings, 
  ChevronLeft, ChevronRight, Save, Trash2, Edit, MapPin,
  Mail, MessageSquare, Phone, AlertTriangle, Check, CalendarCheck
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useWorkspace } from '../contexts/WorkspaceContext';
import api from '@/lib/api';
import { ChantiersARealiser } from '@/components/planning/ChantiersARealiser';
import { FichesTournee } from '@/components/planning/FichesTournee';

const JOURS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const HEURES = Array.from({ length: 14 }, (_, i) => `${i + 6}h`); // 6h à 19h pour plus de détail

// Types d'événements enrichis
const TYPES_EVENEMENT = {
  intervention: { label: 'Intervention', color: 'bg-green-100 text-green-800 border-green-300', icon: Tractor },
  entretien: { label: 'Entretien', color: 'bg-blue-100 text-blue-800 border-blue-300', icon: Settings },
  livraison: { label: 'Livraison', color: 'bg-purple-100 text-purple-800 border-purple-300', icon: MapPin },
  rdv_physique: { label: 'RDV Physique', color: 'bg-orange-100 text-orange-800 border-orange-300', icon: Users },
  rdv_visio: { label: 'Visio', color: 'bg-cyan-100 text-cyan-800 border-cyan-300', icon: MessageSquare },
  deplacement: { label: 'Déplacement', color: 'bg-amber-100 text-amber-800 border-amber-300', icon: MapPin },
  reunion: { label: 'Réunion', color: 'bg-indigo-100 text-indigo-800 border-indigo-300', icon: Users },
  formation: { label: 'Formation', color: 'bg-pink-100 text-pink-800 border-pink-300', icon: Users },
  cuisson: { label: 'Cuisson Distillerie', color: 'bg-red-100 text-red-800 border-red-300', icon: Clock },
  personnel: { label: 'Personnel', color: 'bg-gray-100 text-gray-800 border-gray-300', icon: Calendar }
};

const PlanningPage = () => {
  const { toast } = useToast();
  const { societes, activeSociete } = useWorkspace();
  const [loading, setLoading] = useState(true);
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [viewMode, setViewMode] = useState('semaine'); // semaine, jour, mois
  
  // Données
  const [equipe, setEquipe] = useState([]);
  const [materiels, setMateriels] = useState([]);
  const [taches, setTaches] = useState([]);
  const [conflits, setConflits] = useState([]); // Conflits détectés
  
  // Filtres multi-workspace
  const [selectedSocietes, setSelectedSocietes] = useState([]); // Plusieurs sociétés sélectionnables
  const [showAllWorkspaces, setShowAllWorkspaces] = useState(false);
  const [filterUser, setFilterUser] = useState('all'); // 'all', 'me', ou un ID utilisateur
  
  // Dialogs
  const [showNewTache, setShowNewTache] = useState(false);
  const [showAlertSettings, setShowAlertSettings] = useState(false);
  const [showConflits, setShowConflits] = useState(false);
  const [editingTache, setEditingTache] = useState(null);
  
  // Nouvelle tâche avec plus de types
  const [newTache, setNewTache] = useState({
    titre: '',
    description: '',
    date: '',
    heure_debut: '08:00',
    heure_fin: '09:00',
    type: 'intervention',
    assignee_type: 'personne',
    assignee_id: '',
    parcelle_id: '',
    priorite: 'normale',
    societe_id: '',
    lieu: '',
    lien_visio: '',
    participants: []
  });
  
  // Configuration alertes
  const [alertConfig, setAlertConfig] = useState({
    nouvelle_tache: true,
    modification_tache: true,
    rappel_veille: true,
    rappel_matin: true,
    heure_rappel_matin: '07:00',
    notif_email: true,
    notif_sms: false,
    notif_telegram: true,
    notif_push: true
  });

  // Initialisation avec toutes les sociétés si "showAllWorkspaces"
  useEffect(() => {
    if (activeSociete && !showAllWorkspaces) {
      setSelectedSocietes([activeSociete.id]);
    }
  }, [activeSociete, showAllWorkspaces]);

  // Sélectionner toutes les sociétés
  useEffect(() => {
    if (showAllWorkspaces && societes?.length > 0) {
      setSelectedSocietes(societes.map(s => s.id));
    }
  }, [showAllWorkspaces, societes]);

  useEffect(() => {
    loadData();
  }, [selectedSocietes, currentWeek, filterUser]);

  // P1 iter 127 : Marquer le planning comme "lu" au chargement (briefing soir)
  useEffect(() => {
    api.post('/planning-views/marquer-lu', {}).catch(() => {});
  }, []);

  // Détecter les conflits de planning
  const detecterConflits = (tachesList) => {
    const conflitsDetectes = [];
    
    // Grouper par personne
    const parPersonne = {};
    tachesList.forEach(t => {
      const assignee = t.assignee_id || 'non_assigne';
      if (!parPersonne[assignee]) parPersonne[assignee] = [];
      parPersonne[assignee].push(t);
    });
    
    // Vérifier les chevauchements pour chaque personne
    Object.entries(parPersonne).forEach(([personneId, tachesPersonne]) => {
      for (let i = 0; i < tachesPersonne.length; i++) {
        for (let j = i + 1; j < tachesPersonne.length; j++) {
          const t1 = tachesPersonne[i];
          const t2 = tachesPersonne[j];
          
          // Même jour ?
          const date1 = t1.date?.split('T')[0] || t1.date;
          const date2 = t2.date?.split('T')[0] || t2.date;
          
          if (date1 === date2) {
            // Convertir heures en minutes pour comparaison
            const parseHeure = (h) => {
              if (!h) return 0;
              const [hh, mm] = h.replace('h', ':00').split(':').map(Number);
              return hh * 60 + (mm || 0);
            };
            
            const debut1 = parseHeure(t1.heure_debut);
            const fin1 = parseHeure(t1.heure_fin);
            const debut2 = parseHeure(t2.heure_debut);
            const fin2 = parseHeure(t2.heure_fin);
            
            // Chevauchement si les intervalles se croisent
            if (debut1 < fin2 && debut2 < fin1) {
              conflitsDetectes.push({
                personne_id: personneId,
                tache1: t1,
                tache2: t2,
                date: date1,
                message: `Conflit : "${t1.titre}" et "${t2.titre}" se chevauchent le ${new Date(date1).toLocaleDateString('fr-FR')}`
              });
            }
          }
        }
      }
    });
    
    setConflits(conflitsDetectes);
    return conflitsDetectes;
  };

  const loadData = async () => {
    setLoading(true);
    try {
      // Charger l'équipe
      const equipeRes = await api.get('/team/members');
      setEquipe(equipeRes.data.members || []);
      
      // Charger les matériels avec localisations
      try {
        const locRes = await api.get('/logistique/machines/localisations');
        setMateriels(locRes.data.machines || []);
      } catch (e) {
        // Fallback sur l'ancienne API si la nouvelle n'existe pas
        const matRes = await api.get('/team/materiels');
        setMateriels(matRes.data.materiels || []);
      }
      
      // Charger les tâches de la semaine pour toutes les sociétés sélectionnées
      const weekStart = getWeekStart(currentWeek);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 7);
      
      // Si multi-workspace, charger pour toutes les sociétés
      const societeIds = selectedSocietes.length > 0 ? selectedSocietes.join(',') : '';
      const filterParam = filterUser !== 'all' ? `&user_id=${filterUser}` : '';
      
      const tachesRes = await api.get(`/planning/taches?start=${weekStart.toISOString()}&end=${weekEnd.toISOString()}&societes=${societeIds}${filterParam}`);
      const tachesData = tachesRes.data.taches || [];
      setTaches(tachesData);
      
      // Détecter les conflits
      detecterConflits(tachesData);
      
      // Charger config alertes
      const alertRes = await api.get('/planning/alertes/config');
      if (alertRes.data.config) {
        setAlertConfig(prev => ({ ...prev, ...alertRes.data.config }));
      }
    } catch (err) {
      console.error('Erreur chargement planning:', err);
      // Données de démo
      setEquipe([
        { id: '1', nom: 'Jean Dupont', role: 'Chef d\'exploitation', couleur: '#22c55e' },
        { id: '2', nom: 'Marie Martin', role: 'Tractoriste', couleur: '#3b82f6' },
        { id: '3', nom: 'Pierre Bernard', role: 'Ouvrier agricole', couleur: '#f59e0b' }
      ]);
      setTaches([
        { id: '1', titre: 'Traitement vigne', date: getWeekDayDate(1), heure_debut: '8h', heure_fin: '12h', assignee_id: '1', type: 'intervention', priorite: 'haute' },
        { id: '2', titre: 'Vidange tracteur', date: getWeekDayDate(2), heure_debut: '14h', heure_fin: '16h', assignee_id: '2', type: 'entretien', priorite: 'normale' },
        { id: '3', titre: 'Livraison coopérative', date: getWeekDayDate(3), heure_debut: '9h', heure_fin: '11h', assignee_id: '3', type: 'livraison', priorite: 'normale' }
      ]);
    }
    setLoading(false);
  };

  const getWeekStart = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff));
  };

  const getWeekDayDate = (dayIndex) => {
    const weekStart = getWeekStart(currentWeek);
    const date = new Date(weekStart);
    date.setDate(date.getDate() + dayIndex);
    return date.toISOString().split('T')[0];
  };

  const getWeekDates = () => {
    const dates = [];
    const weekStart = getWeekStart(currentWeek);
    for (let i = 0; i < 7; i++) {
      const date = new Date(weekStart);
      date.setDate(date.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  const previousWeek = () => {
    const newDate = new Date(currentWeek);
    newDate.setDate(newDate.getDate() - 7);
    setCurrentWeek(newDate);
  };

  const nextWeek = () => {
    const newDate = new Date(currentWeek);
    newDate.setDate(newDate.getDate() + 7);
    setCurrentWeek(newDate);
  };

  const getTachesForDay = (date, assigneeId = null) => {
    const dateStr = date.toISOString().split('T')[0];
    return taches.filter(t => {
      const tacheDate = t.date?.split('T')[0] || t.date;
      const matchDate = tacheDate === dateStr;
      const matchAssignee = !assigneeId || t.assignee_id === assigneeId;
      return matchDate && matchAssignee;
    });
  };

  const getTypeColor = (type) => {
    return TYPES_EVENEMENT[type]?.color || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const getTypeLabel = (type) => {
    return TYPES_EVENEMENT[type]?.label || type;
  };

  const getPrioriteIcon = (priorite) => {
    if (priorite === 'haute') return <AlertTriangle className="w-3 h-3 text-red-500" />;
    if (priorite === 'urgente') return <AlertTriangle className="w-3 h-3 text-red-600 animate-pulse" />;
    return null;
  };

  // Toggle sélection d'une société
  const toggleSociete = (societeId) => {
    setSelectedSocietes(prev => {
      if (prev.includes(societeId)) {
        return prev.filter(id => id !== societeId);
      } else {
        return [...prev, societeId];
      }
    });
    setShowAllWorkspaces(false);
  };

  // Sélectionner/désélectionner tout
  const toggleAllWorkspaces = () => {
    if (showAllWorkspaces) {
      setShowAllWorkspaces(false);
      setSelectedSocietes(activeSociete ? [activeSociete.id] : []);
    } else {
      setShowAllWorkspaces(true);
      setSelectedSocietes(societes?.map(s => s.id) || []);
    }
  };

  const handleCreateTache = async () => {
    try {
      await api.post('/planning/taches', {
        ...newTache,
        societe_id: selectedSocietes[0] || activeSociete?.id
      });
      toast({ title: 'Tâche créée', description: 'La tâche a été ajoutée au planning.' });
      setShowNewTache(false);
      setNewTache({
        titre: '',
        description: '',
        date: '',
        heure_debut: '08:00',
        heure_fin: '09:00',
        type: 'intervention',
        assignee_type: 'personne',
        assignee_id: '',
        parcelle_id: '',
        priorite: 'normale',
        societe_id: '',
        lieu: '',
        lien_visio: '',
        participants: []
      });
      loadData();
    } catch (err) {
      // Ajouter localement pour démo
      const newId = String(taches.length + 1);
      setTaches([...taches, { ...newTache, id: newId, societe_id: selectedSocietes[0] }]);
      toast({ title: 'Tâche créée', description: 'La tâche a été ajoutée au planning.' });
      setShowNewTache(false);
      
      // Vérifier conflits après ajout
      detecterConflits([...taches, { ...newTache, id: newId }]);
    }
  };

  const handleSaveAlertConfig = async () => {
    try {
      await api.post('/planning/alertes/config', alertConfig);
      toast({ title: 'Configuration sauvegardée', description: 'Vos préférences de notification ont été enregistrées.' });
      setShowAlertSettings(false);
    } catch (err) {
      toast({ title: 'Configuration sauvegardée', description: 'Vos préférences de notification ont été enregistrées.' });
      setShowAlertSettings(false);
    }
  };

  const formatDateHeader = (date) => {
    const today = new Date();
    const isToday = date.toDateString() === today.toDateString();
    return (
      <div className={`text-center p-2 ${isToday ? 'bg-green-100 rounded-lg' : ''}`}>
        <div className="text-xs text-muted-foreground">{JOURS[date.getDay() === 0 ? 6 : date.getDay() - 1]}</div>
        <div className={`text-lg font-bold ${isToday ? 'text-green-600' : ''}`}>{date.getDate()}</div>
      </div>
    );
  };

  const weekDates = getWeekDates();
  const weekStart = getWeekStart(currentWeek);
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 6);

  return (
    <div className="p-6 space-y-6" data-testid="planning-page">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Calendar className="w-8 h-8 text-green-600" />
            Planning
          </h1>
          <p className="text-muted-foreground mt-1">
            Votre agenda • Équipes • Matériels
          </p>
        </div>
        <div className="flex gap-2 items-center">
          {/* Indicateur de conflits */}
          {conflits.length > 0 && (
            <Button 
              variant="destructive" 
              size="sm"
              onClick={() => setShowConflits(true)}
              className="animate-pulse"
            >
              <AlertTriangle className="w-4 h-4 mr-2" />
              {conflits.length} conflit{conflits.length > 1 ? 's' : ''}
            </Button>
          )}
          <Button variant="outline" onClick={() => setShowAlertSettings(true)}>
            <Bell className="w-4 h-4 mr-2" />
            Alertes
          </Button>
          <Button onClick={() => setShowNewTache(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Nouveau
          </Button>
        </div>
      </div>

      {/* Filtres multi-workspace */}
      <div className="flex flex-col gap-3">
        {/* Sélection des workspaces */}
        {societes && societes.length > 1 && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-muted-foreground">Workspaces :</span>
            <Button
              variant={showAllWorkspaces ? "default" : "outline"}
              size="sm"
              onClick={toggleAllWorkspaces}
            >
              {showAllWorkspaces ? <Check className="w-3 h-3 mr-1" /> : null}
              Tous ({societes.length})
            </Button>
            {societes.map(s => (
              <Button
                key={s.id}
                variant={selectedSocietes.includes(s.id) && !showAllWorkspaces ? "default" : "outline"}
                size="sm"
                onClick={() => toggleSociete(s.id)}
                className="text-xs"
              >
                {selectedSocietes.includes(s.id) && !showAllWorkspaces && <Check className="w-3 h-3 mr-1" />}
                {s.nom}
              </Button>
            ))}
          </div>
        )}

        {/* Filtres supplémentaires et navigation */}
        <div className="flex items-center gap-4 flex-wrap">
          {/* Filtre par personne */}
          <Select value={filterUser} onValueChange={setFilterUser}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filtrer par personne" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tout le monde</SelectItem>
              <SelectItem value="me">Mon planning</SelectItem>
              {equipe.map(m => (
                <SelectItem key={m.id} value={m.id}>{m.nom}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Mode de vue */}
          <Select value={viewMode} onValueChange={setViewMode}>
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="jour">Vue jour</SelectItem>
              <SelectItem value="semaine">Vue semaine</SelectItem>
              <SelectItem value="mois">Vue mois</SelectItem>
            </SelectContent>
          </Select>

          {/* Navigation semaine */}
          <div className="flex items-center gap-2 ml-auto">
            <Button variant="outline" size="icon" onClick={previousWeek}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="px-4 py-2 bg-muted rounded-lg min-w-[200px] text-center">
              <span className="font-medium">
                {weekStart.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                {' - '}
                {weekEnd.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
              </span>
            </div>
            <Button variant="outline" size="icon" onClick={nextWeek}>
              <ChevronRight className="w-4 h-4" />
            </Button>
            <Button variant="outline" onClick={() => setCurrentWeek(new Date())}>
              Aujourd'hui
            </Button>
          </div>
        </div>
      </div>

      <Tabs defaultValue="equipe" className="space-y-4">
        <TabsList>
          <TabsTrigger value="equipe">
            <Users className="w-4 h-4 mr-2" />
            Équipe
          </TabsTrigger>
          <TabsTrigger value="materiel">
            <Tractor className="w-4 h-4 mr-2" />
            Matériel
          </TabsTrigger>
          <TabsTrigger value="logistique">
            <MapPin className="w-4 h-4 mr-2" />
            Logistique
          </TabsTrigger>
          <TabsTrigger value="liste">
            <Calendar className="w-4 h-4 mr-2" />
            Liste
          </TabsTrigger>
          <TabsTrigger value="a-realiser" data-testid="tab-chantiers-a-realiser">
            <CalendarCheck className="w-4 h-4 mr-2" />
            Chantiers à réaliser
          </TabsTrigger>
          <TabsTrigger value="tournee" data-testid="tab-tournee-julien">
            <MapPin className="w-4 h-4 mr-2" />
            Tournée Julien
          </TabsTrigger>
        </TabsList>

        <TabsContent value="tournee">
          <FichesTournee />
        </TabsContent>

        <TabsContent value="a-realiser">
          <ChantiersARealiser equipe={equipe} materiels={materiels} onPlanifie={loadData} />
        </TabsContent>

        {/* Vue Équipe */}
        <TabsContent value="equipe">
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b">
                      <th className="p-3 text-left w-40 bg-muted/50">Personne</th>
                      {weekDates.map((date, idx) => (
                        <th key={idx} className="p-2 border-l min-w-[120px]">
                          {formatDateHeader(date)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {equipe.map(membre => (
                      <tr key={membre.id} className="border-b hover:bg-muted/20">
                        <td className="p-3 font-medium">
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-3 h-3 rounded-full" 
                              style={{ backgroundColor: membre.couleur }}
                            />
                            <div>
                              <div className="font-medium text-sm">{membre.nom}</div>
                              <div className="text-xs text-muted-foreground">{membre.role}</div>
                            </div>
                          </div>
                        </td>
                        {weekDates.map((date, idx) => {
                          const dayTaches = getTachesForDay(date, membre.id);
                          return (
                            <td key={idx} className="p-1 border-l align-top min-h-[80px]">
                              <div className="space-y-1">
                                {dayTaches.map(tache => (
                                  <div 
                                    key={tache.id}
                                    className={`p-2 rounded border text-xs cursor-pointer hover:shadow ${getTypeColor(tache.type)}`}
                                    onClick={() => setEditingTache(tache)}
                                  >
                                    <div className="flex items-center gap-1">
                                      {getPrioriteIcon(tache.priorite)}
                                      <span className="font-medium truncate">{tache.titre}</span>
                                    </div>
                                    <div className="text-[10px] opacity-75">
                                      {tache.heure_debut} - {tache.heure_fin}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Vue Matériel */}
        <TabsContent value="materiel">
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b">
                      <th className="p-3 text-left w-40 bg-muted/50">Matériel</th>
                      {weekDates.map((date, idx) => (
                        <th key={idx} className="p-2 border-l min-w-[120px]">
                          {formatDateHeader(date)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {materiels.slice(0, 10).map(mat => (
                      <tr key={mat.id} className="border-b hover:bg-muted/20">
                        <td className="p-3 font-medium">
                          <div>
                            <div className="font-medium text-sm">{mat.nom}</div>
                            <div className="text-xs text-muted-foreground">{mat.marque} {mat.modele}</div>
                          </div>
                        </td>
                        {weekDates.map((date, idx) => {
                          const dayTaches = taches.filter(t => 
                            t.assignee_type === 'materiel' && 
                            t.assignee_id === mat.id &&
                            (t.date?.split('T')[0] || t.date) === date.toISOString().split('T')[0]
                          );
                          return (
                            <td key={idx} className="p-1 border-l align-top">
                              <div className="space-y-1">
                                {dayTaches.map(tache => (
                                  <div 
                                    key={tache.id}
                                    className={`p-2 rounded border text-xs ${getTypeColor(tache.type)}`}
                                  >
                                    <div className="font-medium truncate">{tache.titre}</div>
                                    <div className="text-[10px] opacity-75">
                                      {tache.heure_debut} - {tache.heure_fin}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Vue Logistique - Localisation et Transport Matériel */}
        <TabsContent value="logistique">
          <div className="space-y-4">
            {/* Carte récap localisation */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="bg-green-50 dark:bg-green-950/30 border-green-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-green-700 dark:text-green-300">
                        {materiels.filter(m => m.localisation?.type === 'depot').length}
                      </p>
                      <p className="text-sm text-green-600 dark:text-green-400">Au dépôt</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-blue-50 dark:bg-blue-950/30 border-blue-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                      <Users className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">
                        {materiels.filter(m => ['client', 'chantier'].includes(m.localisation?.type)).length}
                      </p>
                      <p className="text-sm text-blue-600 dark:text-blue-400">Chez client/chantier</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-amber-50 dark:bg-amber-950/30 border-amber-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900 flex items-center justify-center">
                      <Tractor className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-amber-700 dark:text-amber-300">
                        {materiels.filter(m => m.localisation?.type === 'transit').length}
                      </p>
                      <p className="text-sm text-amber-600 dark:text-amber-400">En transit</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* Liste des matériels avec localisation */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  Localisation du Matériel
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="p-3">Matériel</th>
                        <th className="p-3">Localisation actuelle</th>
                        <th className="p-3">Depuis</th>
                        <th className="p-3">Transport requis</th>
                        <th className="p-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {materiels.filter(m => m.necessite_transport).slice(0, 20).map(mat => (
                        <tr key={mat.id} className="border-b hover:bg-muted/30">
                          <td className="p-3">
                            <div className="font-medium">{mat.nom}</div>
                            <div className="text-xs text-muted-foreground">{mat.marque} {mat.modele}</div>
                          </td>
                          <td className="p-3">
                            <Badge variant={
                              mat.localisation?.type === 'depot' ? 'default' :
                              mat.localisation?.type === 'transit' ? 'destructive' : 'secondary'
                            }>
                              {mat.localisation?.type === 'depot' ? '🏠 Dépôt' :
                               mat.localisation?.type === 'client' ? '👤 Chez client' :
                               mat.localisation?.type === 'chantier' ? '🚧 Chantier' :
                               mat.localisation?.type === 'transit' ? '🚛 En transit' : '❓ Inconnu'}
                            </Badge>
                            {mat.localisation?.adresse && (
                              <div className="text-xs text-muted-foreground mt-1">{mat.localisation.adresse}</div>
                            )}
                          </td>
                          <td className="p-3 text-sm text-muted-foreground">
                            {mat.localisation?.depuis ? 
                              new Date(mat.localisation.depuis).toLocaleDateString('fr-FR') : '-'}
                          </td>
                          <td className="p-3">
                            {mat.necessite_transport ? (
                              <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-300">
                                🚛 Porte-char requis
                              </Badge>
                            ) : (
                              <span className="text-sm text-muted-foreground">Autonome</span>
                            )}
                          </td>
                          <td className="p-3">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => {
                                setNewTache({
                                  ...newTache,
                                  titre: `Déplacer ${mat.nom}`,
                                  type: 'deplacement',
                                  assignee_type: 'materiel',
                                  assignee_id: mat.id
                                });
                                setShowNewTache(true);
                              }}
                            >
                              <MapPin className="w-3 h-3 mr-1" />
                              Déplacer
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
            
            {/* Transports planifiés */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Tractor className="w-5 h-5" />
                  Transports planifiés cette semaine
                </CardTitle>
              </CardHeader>
              <CardContent>
                {taches.filter(t => t.type === 'deplacement').length === 0 ? (
                  <p className="text-center text-muted-foreground py-6">
                    Aucun transport planifié cette semaine
                  </p>
                ) : (
                  <div className="space-y-2">
                    {taches.filter(t => t.type === 'deplacement').map(transport => (
                      <div key={transport.id} className="flex items-center gap-4 p-3 bg-muted/30 rounded-lg">
                        <div className="w-2 h-12 bg-amber-500 rounded" />
                        <div className="flex-1">
                          <div className="font-medium">{transport.titre}</div>
                          <div className="text-sm text-muted-foreground">
                            {new Date(transport.date).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}
                            {' • '}{transport.heure_debut} - {transport.heure_fin}
                          </div>
                        </div>
                        <Badge variant="outline">{transport.statut || 'Planifié'}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Vue Liste */}
        <TabsContent value="liste">
          <Card>
            <CardContent className="p-4">
              <div className="space-y-3">
                {taches.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    Aucune tâche cette semaine
                  </p>
                ) : (
                  taches.map(tache => {
                    const assignee = equipe.find(e => e.id === tache.assignee_id);
                    return (
                      <div 
                        key={tache.id}
                        className="flex items-center gap-4 p-3 border rounded-lg hover:bg-muted/50"
                      >
                        <div className={`w-1 h-12 rounded ${
                          tache.type === 'intervention' ? 'bg-green-500' :
                          tache.type === 'entretien' ? 'bg-blue-500' :
                          tache.type === 'livraison' ? 'bg-purple-500' : 'bg-orange-500'
                        }`} />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            {getPrioriteIcon(tache.priorite)}
                            <span className="font-medium">{tache.titre}</span>
                            <Badge variant="outline" className="text-xs">{tache.type}</Badge>
                          </div>
                          <div className="text-sm text-muted-foreground mt-1">
                            {new Date(tache.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
                            {' • '}{tache.heure_debut} - {tache.heure_fin}
                          </div>
                        </div>
                        {assignee && (
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-3 h-3 rounded-full" 
                              style={{ backgroundColor: assignee.couleur }}
                            />
                            <span className="text-sm">{assignee.nom}</span>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog Nouvelle Tâche */}
      <Dialog open={showNewTache} onOpenChange={setShowNewTache}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nouvelle tâche</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Titre *</Label>
              <Input
                value={newTache.titre}
                onChange={(e) => setNewTache(t => ({ ...t, titre: e.target.value }))}
                placeholder="Ex: Traitement parcelle Nord"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={newTache.date}
                  onChange={(e) => setNewTache(t => ({ ...t, date: e.target.value }))}
                />
              </div>
              <div>
                <Label>Type</Label>
                <Select 
                  value={newTache.type} 
                  onValueChange={(v) => setNewTache(t => ({ ...t, type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="intervention">🌱 Intervention</SelectItem>
                    <SelectItem value="entretien">🔧 Entretien</SelectItem>
                    <SelectItem value="livraison">🚛 Livraison</SelectItem>
                    <SelectItem value="rdv_physique">👥 RDV Physique</SelectItem>
                    <SelectItem value="rdv_visio">💻 Visio</SelectItem>
                    <SelectItem value="deplacement">🚗 Déplacement</SelectItem>
                    <SelectItem value="reunion">📋 Réunion</SelectItem>
                    <SelectItem value="formation">📚 Formation</SelectItem>
                    <SelectItem value="cuisson">🔥 Cuisson Distillerie</SelectItem>
                    <SelectItem value="personnel">📌 Personnel</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Heure début</Label>
                <Input
                  type="time"
                  value={newTache.heure_debut}
                  onChange={(e) => setNewTache(t => ({ ...t, heure_debut: e.target.value }))}
                />
              </div>
              <div>
                <Label>Heure fin</Label>
                <Input
                  type="time"
                  value={newTache.heure_fin}
                  onChange={(e) => setNewTache(t => ({ ...t, heure_fin: e.target.value }))}
                />
              </div>
            </div>
            
            {/* Champs conditionnels selon le type */}
            {(newTache.type === 'rdv_visio') && (
              <div>
                <Label>Lien visio</Label>
                <Input
                  value={newTache.lien_visio || ''}
                  onChange={(e) => setNewTache(t => ({ ...t, lien_visio: e.target.value }))}
                  placeholder="https://meet.google.com/..."
                />
              </div>
            )}
            
            {(newTache.type === 'rdv_physique' || newTache.type === 'deplacement') && (
              <div>
                <Label>Lieu</Label>
                <Input
                  value={newTache.lieu || ''}
                  onChange={(e) => setNewTache(t => ({ ...t, lieu: e.target.value }))}
                  placeholder="Adresse ou lieu du RDV"
                />
              </div>
            )}

            <div>
              <Label>Assigner à</Label>
              <Select 
                value={newTache.assignee_id} 
                onValueChange={(v) => setNewTache(t => ({ ...t, assignee_id: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner une personne" />
                </SelectTrigger>
                <SelectContent>
                  {equipe.map(m => (
                    <SelectItem key={m.id} value={m.id}>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: m.couleur }} />
                        {m.nom}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Priorité</Label>
              <Select 
                value={newTache.priorite} 
                onValueChange={(v) => setNewTache(t => ({ ...t, priorite: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="basse">Basse</SelectItem>
                  <SelectItem value="normale">Normale</SelectItem>
                  <SelectItem value="haute">⚠️ Haute</SelectItem>
                  <SelectItem value="urgente">🚨 Urgente</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                value={newTache.description}
                onChange={(e) => setNewTache(t => ({ ...t, description: e.target.value }))}
                placeholder="Détails de la tâche..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewTache(false)}>Annuler</Button>
            <Button onClick={handleCreateTache} disabled={!newTache.titre || !newTache.date}>
              <Check className="w-4 h-4 mr-2" />
              Créer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Configuration Alertes */}
      <Dialog open={showAlertSettings} onOpenChange={setShowAlertSettings}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5" />
              Paramétrage des notifications
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            {/* Événements déclencheurs */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Événements déclencheurs</Label>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm">Nouvelle tâche assignée</span>
                  <Switch 
                    checked={alertConfig.nouvelle_tache}
                    onCheckedChange={(v) => setAlertConfig(c => ({ ...c, nouvelle_tache: v }))}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Modification d'une tâche</span>
                  <Switch 
                    checked={alertConfig.modification_tache}
                    onCheckedChange={(v) => setAlertConfig(c => ({ ...c, modification_tache: v }))}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Rappel la veille</span>
                  <Switch 
                    checked={alertConfig.rappel_veille}
                    onCheckedChange={(v) => setAlertConfig(c => ({ ...c, rappel_veille: v }))}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Rappel le matin même</span>
                  <Switch 
                    checked={alertConfig.rappel_matin}
                    onCheckedChange={(v) => setAlertConfig(c => ({ ...c, rappel_matin: v }))}
                  />
                </div>
                {alertConfig.rappel_matin && (
                  <div className="ml-4 flex items-center gap-2">
                    <Label className="text-xs">Heure du rappel :</Label>
                    <Input 
                      type="time"
                      value={alertConfig.heure_rappel_matin}
                      onChange={(e) => setAlertConfig(c => ({ ...c, heure_rappel_matin: e.target.value }))}
                      className="w-24 h-8"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Canaux de notification */}
            <div className="space-y-3 pt-4 border-t">
              <Label className="text-sm font-medium">Canaux de notification</Label>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm flex items-center gap-2">
                    <Mail className="w-4 h-4" /> Email
                  </span>
                  <Switch 
                    checked={alertConfig.notif_email}
                    onCheckedChange={(v) => setAlertConfig(c => ({ ...c, notif_email: v }))}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm flex items-center gap-2">
                    <Phone className="w-4 h-4" /> SMS
                  </span>
                  <Switch 
                    checked={alertConfig.notif_sms}
                    onCheckedChange={(v) => setAlertConfig(c => ({ ...c, notif_sms: v }))}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm flex items-center gap-2">
                    <MessageSquare className="w-4 h-4" /> Telegram
                  </span>
                  <Switch 
                    checked={alertConfig.notif_telegram}
                    onCheckedChange={(v) => setAlertConfig(c => ({ ...c, notif_telegram: v }))}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm flex items-center gap-2">
                    <Bell className="w-4 h-4" /> Notification push
                  </span>
                  <Switch 
                    checked={alertConfig.notif_push}
                    onCheckedChange={(v) => setAlertConfig(c => ({ ...c, notif_push: v }))}
                  />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAlertSettings(false)}>Annuler</Button>
            <Button onClick={handleSaveAlertConfig}>
              <Save className="w-4 h-4 mr-2" />
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Conflits de Planning */}
      <Dialog open={showConflits} onOpenChange={setShowConflits}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" />
              Conflits de planning détectés ({conflits.length})
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {conflits.map((conflit, idx) => {
              const personne = equipe.find(e => e.id === conflit.personne_id);
              return (
                <div 
                  key={idx}
                  className="p-4 border border-destructive/30 rounded-lg bg-destructive/5"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="w-4 h-4 text-destructive" />
                    <span className="font-medium text-destructive">
                      {personne?.nom || 'Non assigné'}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      - {new Date(conflit.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
                    </span>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={getTypeColor(conflit.tache1.type)}>
                        {conflit.tache1.heure_debut} - {conflit.tache1.heure_fin}
                      </Badge>
                      <span>{conflit.tache1.titre}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={getTypeColor(conflit.tache2.type)}>
                        {conflit.tache2.heure_debut} - {conflit.tache2.heure_fin}
                      </Badge>
                      <span>{conflit.tache2.titre}</span>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Ces deux événements se chevauchent. Veuillez modifier l'un d'eux.
                  </p>
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button onClick={() => setShowConflits(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PlanningPage;
